import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { EnergyAdvisorCustomerListResponse } from "@/types/energy-advisor";
import type { ClientSort } from "@/types/generated/ClientSort";

export const ENERGY_ADVISOR_CUSTOMERS_KEY = ["energy-advisor", "customers"] as const;

/** Query params for `GET /api/energy-advisor/customers`. Mirrors the server's `EnergyAdvisorListQuery`. */
export interface EnergyAdvisorCustomersParams {
  q?: string;
  page?: number;
  per_page?: number;
  /** `name` (A-Z) or `recent`. Omitted → the server's A-Z default. */
  sort?: ClientSort;
}

function buildQueryString(params: EnergyAdvisorCustomersParams): string {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.per_page) qs.set("per_page", String(params.per_page));
  if (params.sort) qs.set("sort", params.sort);
  const str = qs.toString();
  return str ? `?${str}` : "";
}

export function useEnergyAdvisorCustomers(params: EnergyAdvisorCustomersParams = {}) {
  return useQuery({
    queryKey: [...ENERGY_ADVISOR_CUSTOMERS_KEY, params],
    queryFn: () =>
      api.get<EnergyAdvisorCustomerListResponse>(
        `/api/energy-advisor/customers${buildQueryString(params)}`,
      ),
    // Keep the current page visible while the next page / new search loads,
    // so paging doesn't flash an empty grid.
    placeholderData: keepPreviousData,
  });
}

/**
 * Stamps "I just opened this client", which is what the landing page's
 * Recent order reads.
 *
 * Fire-and-forget: the client page calls this on mount and never renders
 * anything from it. A failed stamp costs the advisor one row of ordering,
 * so it must never surface an error or block the page. It does invalidate
 * the client list, so returning to the landing page shows the new order.
 */
export function useRecordClientVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) =>
      api.post<void>(`/api/energy-advisor/customers/${customerId}/visit`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ENERGY_ADVISOR_CUSTOMERS_KEY });
    },
  });
}
